import { useState, useCallback, useEffect } from "react";
import { useUnreadCount } from "./useUnreadCount";

interface ChatUser {
  id: string;
  /**
   * Etichetta mostrata nell'header della chat. Storicamente il nome, oggi i
   * chiamanti passano l'username (con fallback al nome per gli account che non
   * ne hanno): l'app identifica gli altri utenti per username.
   */
  firstName: string;
  lastName?: string | null;
  profileImageUrl?: string | null;
  isPremium: boolean;
  isOnline: boolean;
  isAiProfile?: boolean;
}

interface ChatWindow {
  id: string;
  user: ChatUser;
  conversationId?: string | null;
  isMinimized: boolean;
}

interface MultiMessengerState {
  openChats: ChatWindow[];
  maxChats: number;
}

// Global state per gestire multiple chat windows
let globalMultiMessengerState: MultiMessengerState = {
  openChats: [],
  maxChats: 3,
};

let globalStateUpdaters: Array<(state: MultiMessengerState) => void> = [];

export function useMultiMessenger() {

  const [messengerState, setMessengerState] = useState<MultiMessengerState>(globalMultiMessengerState);

  // Registra questo component per gli aggiornamenti globali
  useEffect(() => {
    const updateState = (newState: MultiMessengerState) => {
      setMessengerState(newState);
    };

    globalStateUpdaters.push(updateState);

    return () => {
      globalStateUpdaters = globalStateUpdaters.filter(updater => updater !== updateState);
    };
  }, []);

  const unreadCount = useUnreadCount();

  const updateGlobalState = useCallback((newState: MultiMessengerState) => {
    globalMultiMessengerState = newState;
    console.log('Updating global multi-messenger state:', newState);

    // Aggiorna tutti i components registrati
    globalStateUpdaters.forEach(updater => {
      updater(newState);
    });
  }, []);

  const openChat = useCallback((chatUser: ChatUser, conversationId?: string | number | null) => {
    console.log('Opening chat with:', chatUser.id, chatUser.firstName);
    const normalizedConversationId = conversationId ? String(conversationId) : undefined;

    // On mobile, navigate to full-screen chat page instead of opening chat window
    if (window.innerWidth < 1024) {
      // Usa pushState invece di href per navigazione più veloce
      const search = normalizedConversationId
        ? `?conversationId=${encodeURIComponent(normalizedConversationId)}`
        : "";
      window.history.pushState({}, '', `/chat/${chatUser.id}${search}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }

    // Desktop behavior: Verifica se la chat è già aperta
    const existingChatIndex = globalMultiMessengerState.openChats.findIndex(
      chat => chat.user.id === chatUser.id
    );

    let newOpenChats = [...globalMultiMessengerState.openChats];

    if (existingChatIndex >= 0) {
      // La chat è già aperta, mostra se minimizzata
      newOpenChats[existingChatIndex].isMinimized = false;
    } else {
      // Nuova chat
      const newChat: ChatWindow = {
        id: chatUser.id,
        user: chatUser,
        conversationId: normalizedConversationId || null,
        isMinimized: false,
      };

      // Se abbiamo raggiunto il limite massimo, chiudi la prima chat
      if (newOpenChats.length >= globalMultiMessengerState.maxChats) {
        newOpenChats.shift(); // Rimuovi la prima chat
      }

      newOpenChats.push(newChat);
    }

    const newState = {
      ...globalMultiMessengerState,
      openChats: newOpenChats,
    };

    updateGlobalState(newState);
  }, [updateGlobalState]);

  const closeChat = useCallback((userId: string) => {
    console.log('Closing chat with:', userId);

    const newOpenChats = globalMultiMessengerState.openChats.filter(
      chat => chat.user.id !== userId
    );

    const newState = {
      ...globalMultiMessengerState,
      openChats: newOpenChats,
    };

    updateGlobalState(newState);
  }, [updateGlobalState]);

  const minimizeChat = useCallback((userId: string) => {
    console.log('Minimizing chat with:', userId);

    const newOpenChats = globalMultiMessengerState.openChats.map(chat =>
      chat.user.id === userId
        ? { ...chat, isMinimized: true }
        : chat
    );

    const newState = {
      ...globalMultiMessengerState,
      openChats: newOpenChats,
    };

    updateGlobalState(newState);
  }, [updateGlobalState]);

  const expandChat = useCallback((userId: string) => {
    console.log('Expanding chat with:', userId);

    const newOpenChats = globalMultiMessengerState.openChats.map(chat =>
      chat.user.id === userId
        ? { ...chat, isMinimized: false }
        : chat
    );

    const newState = {
      ...globalMultiMessengerState,
      openChats: newOpenChats,
    };

    updateGlobalState(newState);
  }, [updateGlobalState]);

  const closeAllChats = useCallback(() => {
    console.log('Closing all chats');

    const newState = {
      ...globalMultiMessengerState,
      openChats: [],
    };

    updateGlobalState(newState);
  }, [updateGlobalState]);

  return {
    messengerState,
    unreadCount,
    openChats: messengerState.openChats,
    maxChats: messengerState.maxChats,
    openChat,
    closeChat,
    minimizeChat,
    expandChat,
    closeAllChats,
  };
}